import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Brain, Loader2, Save, RotateCcw, Users, Baby, UserRound, PersonStanding, AlertTriangle, Gauge } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorHandler';

const defaultConfig = {
  frequenze_disponibili: ["settimanale", "ogni_10_giorni", "ogni_15_giorni", "mensile", "a_caduta"],
  coeff_adulto: 1.0,
  coeff_bambino_0_3: 0.4,
  coeff_bambino_4_5: 0.5,
  coeff_bambino_6_12: 0.7,
  coeff_ragazzo_13_17: 0.9,
  coeff_anziano: 0.8,
  coeff_neonato_extra: 0.3,
  soglia_scorta_minima_pct: 15.0,
  livello_razionamento: "normale",
  peso_scadenza: 2.0,
};

const frequenzeOptions = [
  { value: "settimanale", label: "Settimanale" },
  { value: "ogni_10_giorni", label: "Ogni 10 giorni" },
  { value: "ogni_15_giorni", label: "Ogni 15 giorni" },
  { value: "mensile", label: "Mensile" },
  { value: "a_caduta", label: "A caduta" },
];

const CoeffSlider = ({ label, icon: Icon, value, onChange, min = 0, max = 2, step = 0.1, desc }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <Label className="text-sm">{label}</Label>
      </div>
      <Badge variant="outline" className="text-xs font-mono">{value.toFixed(1)}x</Badge>
    </div>
    <Slider value={[value]} onValueChange={([v]) => onChange(v)} min={min} max={max} step={step} className="w-full" />
    {desc && <p className="text-[10px] text-muted-foreground">{desc}</p>}
  </div>
);

const AIConfigPage = () => {
  const { API } = useAuth();
  const [config, setConfig] = useState(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/ai-config`);
      setConfig({ ...defaultConfig, ...res.data });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore caricamento configurazione'));
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/ai-config`, config);
      toast.success('Configurazione AI salvata');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore salvataggio'));
    } finally { setSaving(false); }
  };

  const handleReset = () => {
    setConfig(defaultConfig);
    toast.info('Valori riportati ai predefiniti (salva per confermare)');
  };

  const toggleFrequenza = (freq) => {
    const current = config.frequenze_disponibili || [];
    if (current.includes(freq)) {
      if (current.length <= 1) return;
      setConfig({ ...config, frequenze_disponibili: current.filter(f => f !== freq) });
    } else {
      setConfig({ ...config, frequenze_disponibili: [...current, freq] });
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="ai-config-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Configurazione AI</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Regola i parametri dell'intelligenza artificiale per le distribuzioni</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleReset} data-testid="ai-reset-btn">
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="ai-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Salva
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Coefficienti Fabbisogno */}
        <Card>
          <CardHeader className="pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              <CardTitle className="text-base">Coefficienti Fabbisogno</CardTitle>
            </div>
            <CardDescription className="text-xs">Quanto "pesa" ogni componente del nucleo familiare nel calcolo del pacco. 1.0 = porzione intera.</CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-5">
            <CoeffSlider label="Adulto (18-64)" icon={UserRound} value={config.coeff_adulto}
              onChange={v => setConfig({...config, coeff_adulto: v})} desc="Porzione base di riferimento" />
            <CoeffSlider label="Bambino 0-3 anni" icon={Baby} value={config.coeff_bambino_0_3}
              onChange={v => setConfig({...config, coeff_bambino_0_3: v})} desc="Neonati e bambini piccoli" />
            <CoeffSlider label="Bambino 4-5 anni" icon={Baby} value={config.coeff_bambino_4_5}
              onChange={v => setConfig({...config, coeff_bambino_4_5: v})} />
            <CoeffSlider label="Bambino 6-12 anni" icon={Users} value={config.coeff_bambino_6_12}
              onChange={v => setConfig({...config, coeff_bambino_6_12: v})} />
            <CoeffSlider label="Ragazzo 13-17 anni" icon={Users} value={config.coeff_ragazzo_13_17}
              onChange={v => setConfig({...config, coeff_ragazzo_13_17: v})} />
            <CoeffSlider label="Anziano (65+)" icon={PersonStanding} value={config.coeff_anziano}
              onChange={v => setConfig({...config, coeff_anziano: v})} />
            <CoeffSlider label="Extra neonato" icon={Baby} value={config.coeff_neonato_extra}
              onChange={v => setConfig({...config, coeff_neonato_extra: v})} desc="Bonus aggiuntivo per prodotti specifici neonati (pannolini, latte, ecc.)" />
          </CardContent>
        </Card>

        <div className="space-y-4 sm:space-y-6">
          {/* Frequenze */}
          <Card>
            <CardHeader className="pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-primary" />
                <CardTitle className="text-base">Frequenze Distribuzione</CardTitle>
              </div>
              <CardDescription className="text-xs">Seleziona le frequenze disponibili per la tua chiesa.</CardDescription>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
              <div className="flex flex-wrap gap-2">
                {frequenzeOptions.map(f => {
                  const active = (config.frequenze_disponibili || []).includes(f.value);
                  return (
                    <button key={f.value} onClick={() => toggleFrequenza(f.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        active ? 'bg-primary text-white border-primary' : 'bg-muted/50 text-muted-foreground border-border hover:border-primary/50'
                      }`} data-testid={`freq-${f.value}`}>
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Magazzino e Razionamento */}
          <Card>
            <CardHeader className="pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <CardTitle className="text-base">Magazzino e Razionamento</CardTitle>
              </div>
              <CardDescription className="text-xs">Controlla come l'AI gestisce le scorte limitate.</CardDescription>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-5">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Soglia scorta minima</Label>
                  <Badge variant="outline" className="text-xs font-mono">{config.soglia_scorta_minima_pct.toFixed(0)}%</Badge>
                </div>
                <Slider value={[config.soglia_scorta_minima_pct]} onValueChange={([v]) => setConfig({...config, soglia_scorta_minima_pct: v})}
                  min={5} max={50} step={5} />
                <p className="text-[10px] text-muted-foreground">L'AI evita di suggerire prodotti sotto questa % della scorta minima</p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Livello razionamento</Label>
                <Select value={config.livello_razionamento} onValueChange={v => setConfig({...config, livello_razionamento: v})}>
                  <SelectTrigger data-testid="razionamento-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basso">Basso - Porzioni generose</SelectItem>
                    <SelectItem value="normale">Normale - Equilibrato</SelectItem>
                    <SelectItem value="alto">Alto - Conservativo (scorte limitate)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Priorita Scadenze */}
          <Card>
            <CardHeader className="pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
              <div className="flex items-center gap-2">
                <Gauge className="w-5 h-5 text-red-500" />
                <CardTitle className="text-base">Priorita Scadenze</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Peso prodotti in scadenza</Label>
                  <Badge variant="outline" className="text-xs font-mono">{config.peso_scadenza.toFixed(1)}x</Badge>
                </div>
                <Slider value={[config.peso_scadenza]} onValueChange={([v]) => setConfig({...config, peso_scadenza: v})}
                  min={1} max={5} step={0.5} />
                <p className="text-[10px] text-muted-foreground">Quanto l'AI favorisce i prodotti vicini alla scadenza. Piu alto = piu priorita ai prodotti che scadono presto.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AIConfigPage;
